import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listSubjectConfigs } from "../../api/examApi";
import { listStudents } from "../../api/studentApi";
import { listSubjectConfigurations } from "../../api/subjectConfigurationApi";
import "../Attendance/Attendance.css";
import "../Routine/ClassRoutine.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");

/**
 * Direct port of digicampusbd's `exam-mark-input-multiple-blank-sheet`
 * ("Exam Mark Blank Sheet") — a blank grid to print for manual mark
 * collection: real students down the rows, one real short-code column per
 * subject (e.g. Written/MCQ/Practical, from the exam's already-configured
 * `ExamSubjectConfiguration`), all left empty. Despite living under a
 * `/jasper/...`-shaped old-app endpoint, that endpoint already returns plain
 * JSON (student list + subject/short-code structure only, no marks) — the
 * old template renders the blank grid itself, so this was already an inline
 * view in the old app. Reuses `GET /api/exam-config/subject-configs` and
 * `GET /api/students` — no new backend work.
 *
 * Also scopes which SUBJECTS get a blank column by the selected group(s),
 * matching the old app's real `downloadExamMarkInputBlankSheetWithMultipleSubject`
 * (which intersects the user-picked subjects against that class+group's real
 * `SubjectConfiguration` rows) — previously missing here, which offered every
 * exam-configured subject as a column regardless of group (e.g. a
 * Commerce-only subject would still get a blank column for a Science-only
 * print run). The old app takes a single `groupId`; this page already lets
 * staff pick several groups at once (a pre-existing, more convenient
 * consolidation of the old app's own per-group print run), so the scoping
 * here is the natural generalization: a subject stays offered if it's
 * configured for AT LEAST ONE of the selected groups. If none of the
 * selected groups have any `SubjectConfiguration` rows at all, falls back to
 * showing every subject (same permissive-fallback rationale as
 * `ExamMarkServiceImpl#resolveScopedStudents`) rather than presenting an
 * empty, confusing subject list.
 */
export default function ExamMarkBlankSheetPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [subjectIds, setSubjectIds] = useState([]);
  const [groupConfiguredSubjectIds, setGroupConfiguredSubjectIds] = useState(null);

  const [students, setStudents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    groupApi.list().then(setGroups);
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigId, classConfigs]);

  useEffect(() => {
    if (!examConfigId) {
      setAllSubjects([]);
      return;
    }
    listSubjectConfigs(examConfigId).then((subjects) => {
      setAllSubjects(subjects);
      setSubjectIds(subjects.map((s) => String(s.subjectId)));
    });
  }, [examConfigId]);

  const toggleGroup = (id) => setGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  const toggleSubject = (id) => setSubjectIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  // Union, across every selected group, of the subjects that class+group
  // actually has a SubjectConfiguration row for — see doc comment above.
  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc || groupIds.length === 0) {
      setGroupConfiguredSubjectIds(null);
      return;
    }
    let cancelled = false;
    Promise.all(groupIds.map((groupId) => listSubjectConfigurations(cc.classId, groupId))).then((results) => {
      if (cancelled) return;
      const union = new Set();
      results.forEach((rows) => rows.forEach((r) => union.add(String(r.subjectId))));
      setGroupConfiguredSubjectIds(union);
    });
    return () => {
      cancelled = true;
    };
  }, [groupIds, classConfigId, classConfigs]);

  // Every exam-configured subject stays offered when no group is selected, or
  // when the selected group(s) have no Subject Setup rows at all (permissive
  // fallback); otherwise only subjects configured for at least one selected group.
  const visibleSubjects =
    groupConfiguredSubjectIds === null || groupConfiguredSubjectIds.size === 0
      ? allSubjects
      : allSubjects.filter((s) => groupConfiguredSubjectIds.has(String(s.subjectId)));

  // Keep `subjectIds` in sync when the visible set narrows (e.g. after picking a group).
  useEffect(() => {
    const visibleIds = new Set(visibleSubjects.map((s) => String(s.subjectId)));
    setSubjectIds((prev) => prev.filter((id) => visibleIds.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupConfiguredSubjectIds]);

  const selectedSubjects = visibleSubjects.filter((s) => subjectIds.includes(String(s.subjectId)));

  const handleSearch = async () => {
    setError("");
    setStudents(null);
    if (!classConfigId || !academicYear || selectedSubjects.length === 0) {
      setError("Please select section, exam, and at least one subject.");
      return;
    }
    setLoading(true);
    try {
      const data = await listStudents(academicYear, classConfigId);
      const filtered = data
        .filter((s) => groupIds.length === 0 || groupIds.includes(String(s.groupId)))
        .sort((a, b) => a.studentRoll - b.studentRoll);
      if (selectedSubjects.some((s) => !s.markConfigs || s.markConfigs.length === 0)) {
        setError("Mark Configuration Not Found");
      }
      setStudents(filtered);
    } catch (err) {
      setError("Failed to load students.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header no-print">
        <h1>Exam Mark Blank Sheet</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters no-print">
        <label>
          Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-row no-print">
        <span className="admit-card-label">Select Group:</span>{" "}
        {groups.map((g) => (
          <label key={g.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={groupIds.includes(String(g.id))} onChange={() => toggleGroup(String(g.id))} />
            {g.name}
          </label>
        ))}
      </div>
      <div className="form-row no-print">
        <span className="admit-card-label">Select Subject:</span>{" "}
        {visibleSubjects.map((s) => (
          <label key={s.subjectId} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={subjectIds.includes(String(s.subjectId))} onChange={() => toggleSubject(String(s.subjectId))} />
            {s.subjectName}
          </label>
        ))}
        <button type="button" className="btn-primary" onClick={handleSearch} style={{ marginLeft: 12 }}>
          Search
        </button>
        {students && (
          <button type="button" className="btn-secondary" onClick={() => window.print()} style={{ marginLeft: 8 }}>
            Print
          </button>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        students && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th rowSpan={2}>Student Name</th>
                  <th rowSpan={2}>Student ID</th>
                  <th rowSpan={2}>Roll</th>
                  {selectedSubjects.map((s) => (
                    <th key={s.subjectId} colSpan={(s.markConfigs || []).length || 1}>
                      {s.subjectName}
                    </th>
                  ))}
                  <th rowSpan={2}>Total Mark</th>
                </tr>
                <tr>
                  {selectedSubjects.flatMap((s) =>
                    (s.markConfigs && s.markConfigs.length > 0
                      ? s.markConfigs
                      : [{ markConfigId: `${s.subjectId}-none`, shortCodeTitle: "-", totalMark: 0 }]
                    ).map((mc) => (
                      <th key={mc.markConfigId}>
                        {mc.shortCodeTitle} ({mc.totalMark})
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.identificationId}>
                    <td>{s.studentName}</td>
                    <td>{s.customStudentId}</td>
                    <td>{s.studentRoll}</td>
                    {selectedSubjects.flatMap((subj) =>
                      (subj.markConfigs && subj.markConfigs.length > 0 ? subj.markConfigs : [{ markConfigId: `${subj.subjectId}-none` }]).map((mc) => (
                        <td key={mc.markConfigId}></td>
                      ))
                    )}
                    <td></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {students && students.length === 0 && <p>No students found for this selection.</p>}
    </MainLayout>
  );
}
